import { NON_ZONE_HELP, ZONE_META, type ZoneDef } from '../../layout/zones.js';

export interface ZoneHelpText {
  title: string;
  body: string;
}

/** Minimal slice of TableState the resolver needs. */
export interface ResolverState {
  phase: 'COMEOUT' | 'POINT' | string;
  point?: number | undefined;
  bets: ReadonlyArray<{ type: string; number?: number; amount: number; id: string }>;
}

/**
 * Maps a zone the user is asking about to the right help copy. When in POINT
 * phase and the user is looking at the parent line of an existing bet, we
 * surface the ODDS / DON'T ODDS explanation instead — that's what tapping
 * the same zone again would actually do.
 *
 * Returns null when nothing in the copy decks matches (e.g. an unknown zone
 * id). Callers should treat null as "no tooltip".
 */
export function resolveZoneHelp(def: ZoneDef, state: ResolverState): ZoneHelpText | null {
  const oddsTarget = pickOddsParent(state, def);
  if (oddsTarget) {
    const entry = oddsTarget.kind === 'odds' ? NON_ZONE_HELP.odds : NON_ZONE_HELP.dontOdds;
    return { title: entry.helpTitle, body: entry.helpText };
  }
  if (def.id === 'pass') {
    return { title: NON_ZONE_HELP.pass.helpTitle, body: NON_ZONE_HELP.pass.helpText };
  }
  const meta = ZONE_META[def.id];
  if (!meta) return null;
  return { title: meta.helpTitle, body: meta.helpText };
}

function pickOddsParent(
  state: ResolverState,
  def: ZoneDef,
): { kind: 'odds' | 'dontOdds' } | undefined {
  if (def.type === 'pass' || def.type === 'dontPass') {
    if (state.phase !== 'POINT' || state.point === undefined) return undefined;
    const parent = state.bets.find((b) => b.type === def.type);
    if (!parent) return undefined;
    return { kind: def.type === 'pass' ? 'odds' : 'dontOdds' };
  }
  if ((def.type === 'come' || def.type === 'dontCome') && def.number !== undefined) {
    const parent = state.bets.find((b) => b.type === def.type && b.number === def.number);
    if (!parent) return undefined;
    return { kind: def.type === 'come' ? 'odds' : 'dontOdds' };
  }
  return undefined;
}
